export interface SSHConnection {
    connect(host: string, username: string, password: string, rows: number, cols: number): void;
    readFromTerminal(str: string): void;
    readFromNetwork(buf: Uint8Array): void;
    resize(rows: number, cols: number): void;
}

function callFunc(func: (...args: any[]) => any, memory: ArrayBuffer, mallocFunc: (n: number) => number, ...args: any[]) {
    const funcArgs = [];
    const encoder = new TextEncoder();
    for (const arg of args) {
        if (typeof arg === 'string') {
            // If the arg is a string, we need to allocate memory for it, copy it
            // over, and send its start pointer and length as args.
            const encoded = encoder.encode(arg);
            const addr = mallocFunc(encoded.length);
            const buf = new Uint8Array(memory, addr, encoded.length);
            buf.set(encoded);
            funcArgs.push(addr);
            funcArgs.push(arg.length);
        } else {
            // Otherwise, just assume it's a primitive type and append it to the
            // args.
            funcArgs.push(arg);
        }
    }
    func(...funcArgs);
}

export async function createSshConnection(sendToTerminal: (buf: Uint8Array) => void, sendToPeer: (buf: Uint8Array) => void, sessionOpened: () => void, sessionClosed: () => void): Promise<SSHConnection> {
    const go = new Go();
    go.importObject.env = {
        sendToNetwork: (n: number) => {
            console.log(`Sending ${n} bytes to peer...`);
            sendToPeer(outgoingNetworkBuffer().slice(0, n));
        },
        sendToTerminal: (n: number) => {
            console.log('Sending to terminal...');
            sendToTerminal(outgoingTerminalBuffer().slice(0, n));
        },
        sessionOpened: () => {
            console.log('Session opened');
            sessionOpened();
        },
        sessionClosed: () => {
            console.log('Session closed');
            sessionClosed();
        }
    };
    const result = await WebAssembly.instantiateStreaming(fetch('main.wasm'), go.importObject);
    const inst = result.instance;

    go.run(inst);

    const exports = inst.exports as any;
    console.log(exports);
    const memory = exports.memory as WebAssembly.Memory;

    // Make these functions to evaluate at access time, as the underlying memory
    // buffer may change.
    const bufferSize = exports.getBufferSize();
    const incomingNetworkBuffer = () => new Uint8Array(memory.buffer, exports.getIncomingNetworkBuffer(), bufferSize);
    const outgoingNetworkBuffer = () => new Uint8Array(memory.buffer, exports.getOutgoingNetworkBuffer(), bufferSize);
    const incomingTerminalBuffer = () => new Uint8Array(memory.buffer, exports.getIncomingTerminalBuffer(), bufferSize);
    const outgoingTerminalBuffer = () => new Uint8Array(memory.buffer, exports.getOutgoingTerminalBuffer(), bufferSize);

    const encoder = new TextEncoder();
    return {
        connect: (host: string, username: string, password: string, rows: number, cols: number) => {
            callFunc(exports.connect, memory.buffer, exports.malloc, host, username, password, rows, cols);
        },
        readFromTerminal: (str: string) => {
            const buf = encoder.encode(str);
            incomingTerminalBuffer().set(buf);
            exports.receiveFromTerminal(buf.length);
        },
        readFromNetwork: (buf: Uint8Array) => {
            console.log(`Reading ${buf.length} bytes from network...`);

            // If the number of received bytes exceeds the incoming buffer size,
            // send it to the Go side in chunks.
            for (let i = 0; i < buf.length; i += bufferSize) {
                const subChunk = buf.slice(i, Math.min(i + bufferSize, buf.length));
                incomingNetworkBuffer().set(subChunk);
                exports.receiveFromNetwork(subChunk.length);
            }
        },
        resize: (rows: number, cols: number) => {
            callFunc(exports.resize, memory.buffer, exports.malloc, rows, cols);
        },
    };
}
